import type React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { navigate, useMatch } from '@xing-com/crate-core-router';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { useBreakpoints } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useExperiment } from '@xing-com/hub';
import {
  IconAIProfileSearch,
  IconAIFilled,
  IconAICareerChanger,
} from '@xing-com/icons';
import { mediaConfined, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { spaceM, spaceXS } from '@xing-com/tokens';

import { FindJobsTipBox, FindJobsTipBoxProps } from '../find-jobs-tip-box';

import { Tag, Banner } from './promotions.styles';
import { SearchAlertPromotions } from './search-alert-promotions';

type PromotionProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  kind: FabiJobSearchQueryGenerationUseCase;
  tracking: { element: string; sc_o: string };
  showNewMarker?: boolean;
};

const Promotion: React.FC<PromotionProps> = ({
  title,
  kind,
  Icon,
  tracking,
  showNewMarker,
}) => {
  const trackNwt = useJobsNwtTracking();
  const isFindJobs = useMatch(ROUTES.findJobs);

  const onClick = (): void => {
    trackNwt({
      event: 'opened',
      element: tracking.element,
      elementDetail: 'pill',
      componentName: 'ai_search',
      eventSchema: 'aicomponent',
      page: isFindJobs ? 'jobs/index' : 'jobs_search/serp',
    });

    const params = new URLSearchParams({
      [AI_SEARCH_INIT_PROMPT_PARAM]: kind,
      sc_o: tracking.sc_o,
      sc_o_PropActionOrigin: isFindJobs
        ? 'jobs_find_jobs_pill'
        : 'jobs_serp_pill',
    });
    navigate(`${ROUTES.searchAi}?${params.toString()}`);
  };

  return (
    <Tag leadingIcon={Icon} hideBorder onClick={onClick}>
      <FormattedMessage id={title} />
      {showNewMarker ? (
        <Marker icon={IconAIFilled} variant="strong" aria-hidden="true">
          <FormattedMessage id="XDS_FLAG_NEW_NEUTRAL" />
        </Marker>
      ) : null}
    </Tag>
  );
};

type Props =
  | ({ tipbox: true; isSearchAndShuffle?: boolean } & Omit<
      FindJobsTipBoxProps,
      'show' | 'variant'
    >)
  | { tipbox?: false; isSearchAndShuffle?: boolean };

export const Promotions: React.FC<Props> = (props) => {
  const isAiSearchForced = useExperiment('ABACUS-468') === 'B';
  const { isMediaSmallOrTiny } = useBreakpoints();

  const swimpPromotion = (
    <Promotion
      Icon={IconAIProfileSearch}
      title="JOBS_SEARCH_AI_SWIMP_SUGGESTION"
      kind={FabiJobSearchQueryGenerationUseCase.SearchWithProfile}
      tracking={{
        element: 'search_with_my_profile',
        sc_o: 'search_with_my_profile_click',
      }}
      showNewMarker={!props.isSearchAndShuffle}
    />
  );
  const swimsPromotion = (
    <Promotion
      Icon={IconAICareerChanger}
      title="JOBS_SEARCH_AI_SWIMS_SUGGESTION"
      kind={FabiJobSearchQueryGenerationUseCase.SearchWithSkills}
      tracking={{
        element: 'search_with_my_skills',
        sc_o: 'search_with_my_skills_click',
      }}
      showNewMarker={!props.isSearchAndShuffle}
    />
  );

  const { tipbox, isSearchAndShuffle } = props;
  if (tipbox && !isMediaSmallOrTiny) {
    const { onClickNext, onClose } = props;

    return (
      <Container $isSearchAndShuffle={isSearchAndShuffle}>
        {swimpPromotion}
        <FindJobsTipBox
          variant="promotions"
          onClickNext={onClickNext}
          onClose={onClose}
          show
        >
          {swimsPromotion}
        </FindJobsTipBox>
      </Container>
    );
  }

  return (
    <Container $isSearchAndShuffle={isSearchAndShuffle}>
      {swimpPromotion}
      {swimsPromotion}
      {props.isSearchAndShuffle && <SearchAlertPromotions />}
      {isAiSearchForced || props.isSearchAndShuffle ? null : (
        <Banner variant="xsmall" propActionOrigin="jobs_find_jobs" />
      )}
    </Container>
  );
};

const Container = styled.div<{ $isSearchAndShuffle?: boolean }>`
  display: flex;
  gap: ${spaceM};
  margin-top: 20px;
  padding: ${spaceXS} 20px;
  overflow: scroll;
  scrollbar-width: none;

  & [data-xds='TipBox'] {
    max-width: 220px;
  }

  @media ${mediaConfined} {
    justify-content: center;
  }

  ${({ $isSearchAndShuffle }) =>
    $isSearchAndShuffle &&
    css`
      @media ${mediaTopBarVisible} {
        margin-top: ${spaceM};
      }
    `};
`;
